import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddUser() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    referral_code_used: ''
  });
  const [loading, setLoading] = useState(false);
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/admin/users`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('User created successfully!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-8 text-foreground hover:text-primary"
        >
          <ArrowLeft className="mr-2" /> Back to Admin Panel
        </Button>

        <div className="wm-card p-8 rounded-lg card-shadow border border-primary/20">
          <h1 className="text-3xl font-playfair font-bold text-foreground mb-6">
            Add New User
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-foreground">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1 bg-input border-border text-foreground"
                data-testid="name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 bg-input border-border text-foreground"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="mt-1 bg-input border-border text-foreground"
                data-testid="password-input"
              />
            </div>

            <div>
              <Label htmlFor="referral_code" className="text-foreground">
                Referral Code (Optional)
              </Label>
              <Input
                id="referral_code"
                value={formData.referral_code_used}
                onChange={(e) => setFormData({ ...formData, referral_code_used: e.target.value })}
                className="mt-1 bg-input border-border text-foreground"
                placeholder="Enter referral code if referred"
                data-testid="referral-code-input"
              />
            </div>

            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Note:</strong> The user will be created with 
                "No Membership" tier by default. They can upgrade their membership later. 
                A unique referral code will be automatically generated for them.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              data-testid="submit-btn"
            >
              {loading ? 'Creating User...' : 'Create User'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
